import { useQuery, useStatus } from '@powersync/react';

import { queries } from '@flow/sync';

import type { LocalDatabase } from '../db/powersync.js';
import { t } from '../i18n/es.js';

/**
 * Sync state in the shell (SYN-01, docs/05 §6.3): synced, pending changes, offline, or a
 * recoverable error with an action. Every state carries a word and a symbol, never colour alone,
 * and nothing here blocks the app. The F0 diagnostics (storage, first sync time) stay in the DOM
 * for the test tooling but only show when they are a warning the user must see.
 */
export function SyncStatus({ local }: { local: LocalDatabase }) {
  const status = useStatus();
  // A database that already had server data when it was opened is not doing a first sync: the
  // SDK's `hasSynced` arrives a moment later and must not be read as «never synced».
  const everSynced = local.hadSynced ? true : status.hasSynced === true;
  const pendingQuery = queries.pendingUploads();
  const { data } = useQuery<{ n: number }>(pendingQuery.sql, pendingQuery.params);
  const pending = data[0]?.n ?? 0;

  const failure =
    status.dataFlowStatus.uploadError ?? status.dataFlowStatus.downloadError ?? undefined;
  const state: 'error' | 'offline' | 'syncing' | 'pending' | 'synced' = failure
    ? 'error'
    : !status.connected && !status.connecting
      ? 'offline'
      : !everSynced || status.dataFlowStatus.uploading || status.dataFlowStatus.downloading
        ? 'syncing'
        : pending > 0
          ? 'pending'
          : 'synced';

  const connection = status.connected
    ? t.sync.connected
    : status.connecting
      ? t.sync.connecting
      : t.sync.disconnected;
  const flow = !everSynced
    ? t.sync.firstSync
    : status.dataFlowStatus.uploading
      ? t.sync.uploading
      : status.dataFlowStatus.downloading
        ? t.sync.downloading
        : null;
  const storage = local.vfs.fallback
    ? t.sync.storage.memoryFallback
    : local.persisted === true
      ? t.sync.storage.persisted
      : local.persisted === false
        ? t.sync.storage.notPersisted
        : t.sync.storage.unsupported;

  const retry = () => {
    void local.db.disconnect().then(() => local.db.connect(local.connector));
  };

  return (
    <div className="sync-status" data-state={state} aria-live="polite">
      <p className="sync-status__line">
        <span className="sync-status__symbol" aria-hidden="true">
          {SYMBOLS[state]}
        </span>
        <span
          data-testid="sync-connection"
          data-connected={status.connected}
          data-has-synced={status.hasSynced ?? false}
          data-first-sync-ms={
            status.lastSyncedAt ? Math.max(0, status.lastSyncedAt.getTime() - local.openedAt) : ''
          }
        >
          {connection}
        </span>
        <span data-testid="sync-pending" data-pending={pending}>
          {/* «Todo sincronizado» would be wrong while the first download is still running. */}
          {everSynced || pending > 0 ? ` · ${t.sync.pending(pending)}` : null}
        </span>
      </p>
      {flow ? (
        <p className="sync-status__line muted" data-testid="sync-flow">
          {flow}
        </p>
      ) : null}
      {failure ? (
        <p className="sync-status__line" role="alert">
          <span>{t.sync.error}</span>{' '}
          <button type="button" onClick={retry}>
            {t.sync.retry}
          </button>
        </p>
      ) : null}
      {status.lastSyncedAt && !local.hadSynced ? (
        <span data-testid="sync-first-duration" hidden>
          {t.sync.firstSyncTook(Math.max(0, status.lastSyncedAt.getTime() - local.openedAt) / 1000)}
        </span>
      ) : null}
      <p
        className="sync-status__line sync-status__warning"
        data-testid="sync-storage"
        data-vfs={local.vfs.name}
        data-persisted={String(local.persisted)}
        // Only the «not saved on this device» warning matters to the user; the rest is diagnostics.
        hidden={!local.vfs.fallback}
      >
        {t.sync.vfs(local.vfs.name)} · {storage}
      </p>
    </div>
  );
}

const SYMBOLS = {
  synced: '✓',
  pending: '↑',
  syncing: '⟳',
  offline: '○',
  error: '!',
} as const;
